import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../redux/store";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import {
    fetchBudgetsMandatari,
    BudgetsMandatariList,
    resetBudgets,
} from "./budgetsSlice";
import {
    FiltriBudgets,
    setFiltriBudgets,
} from "components/userPreference/userPreferenceSlice";
import { ErrorStatusTypes } from "components/common/errorsDeclarations";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { theme } from "components/theme";
import { Stagione } from "components/anagrafiche/anagraficheSlice";
import { convertToDateTime } from "../../../common/dateUtils/convertToDateTime";

// ============================================
// HELPER - Formattazione valuta
// ============================================

const formatCurrency = (value: number | null | undefined): string => {
    return "€ " + Intl.NumberFormat("it-IT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value || 0);
};

const formatCompact = (value: number): string => {
    return new Intl.NumberFormat("it-IT", {
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

// ============================================
// TYPES
// ============================================

interface ChartDataItem {
    nome: string;
    mandatarioId: number | null;
    valoreBudget: number;
    valoreConfermato: number;
    valoreProdotto: number;
}

// ============================================
// CUSTOM TOOLTIP
// ============================================

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        name: string;
        value: number;
        color: string;
        dataKey: string;
        payload?: ChartDataItem;
    }>;
    label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
        <Box sx={{
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
        }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {label}
            </Typography>
            {payload.map((entry, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: entry.color,
                        borderRadius: '2px',
                    }} />
                    <Typography variant="body2">
                        {entry.name}: {formatCurrency(entry.value)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// ============================================
// CUSTOM LEGEND
// ============================================

const CustomLegend: React.FC = () => {
    const items = [
        { label: 'Budget', color: theme.palette.warning.light },
        { label: 'Confermato', color: theme.palette.success.main },
        { label: 'Prodotto', color: theme.palette.primary.main },
    ];

    return (
        <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
            mb: 1,
        }}>
            {items.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: item.color,
                        borderRadius: '2px',
                    }} />
                    <Typography variant="caption" sx={{ fontSize: 11 }}>
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// ============================================
// COMPONENT
// ============================================

const BudgetMandatarioBarChart = () => {
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================

    const budgetsMandatari = useSelector<RootState, BudgetsMandatariList>(
        (state) => state.budgetsState.budgetsMandatariList
    );

    const filtri = useSelector<RootState, FiltriBudgets>(
        (state) => state.userPreferenceState.filtriBudgets || {}
    );

    const stagioni = useSelector<RootState, Stagione[]>(
        (state) => state.anagraficheState?.stagioni?.results || []
    );

    // ============================================
    // STATI LOCALI
    // ============================================

    const [stagioneSelezionata, setStagioneSelezionata] = useState<number | undefined>(
        filtri?.stagione
    );

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        if (stagioneSelezionata) {
            const nuoviFiltri: FiltriBudgets = {
                ...filtri,
                stagione: stagioneSelezionata,
            };
            dispatch(setFiltriBudgets(nuoviFiltri));
            dispatch(resetBudgets());
            dispatch(fetchBudgetsMandatari({ stagione_id: stagioneSelezionata }));
        }
    }, [stagioneSelezionata, dispatch]);

    // ============================================
    // PREPARAZIONE DATI
    // ============================================

    const prepareChartData = (): ChartDataItem[] => {
        const budgets = budgetsMandatari.budgetsMandatari || [];

        return budgets.map(b => ({
            nome: b.mandatario_display || `Mandatario ${b.mandatario}`,
            mandatarioId: b.mandatario,
            valoreBudget: b.valore_budget || 0,
            valoreConfermato: b.valore_confermato || 0,
            valoreProdotto: b.valore_prodotto || 0,
        }));
    };

    const chartData = prepareChartData();

    // Altezza dinamica: 80px per mandatario (3 barre da ~20px + gap)
    // minimo 400px per non collassare troppo
    const chartHeight = Math.max(400, chartData.length * 80);

    // ============================================
    // HANDLERS
    // ============================================

    const handleStagioneChange = (_: React.SyntheticEvent, newValue: Stagione | null) => {
        setStagioneSelezionata(newValue?.id);
    };

    const isLoading = budgetsMandatari.errorsStack.status === ErrorStatusTypes.PENDING;
    const hasData = chartData.length > 0;

    const totali = budgetsMandatari.totali;

    // ============================================
    // RENDER
    // ============================================

    return (
        <Card raised sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <CardContent sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 2, sm: 3 },
            }}>
                {/* Header */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium' }}>
                            Budget per Mandatario
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Confronto budget, confermato e prodotto per mandatario
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            {convertToDateTime(new Date())?.toFormat('DD')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <Autocomplete
                            size="small"
                            value={stagioni.find(s => s.id === stagioneSelezionata) || null}
                            options={stagioni}
                            getOptionLabel={(option) => option.descrizione || ""}
                            onChange={handleStagioneChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{
                                        backgroundColor: stagioneSelezionata
                                            ? theme.palette.action.selected
                                            : 'inherit',
                                        borderRadius: 1,
                                    }}
                                    label="Stagione"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    {/* KPI sintetici */}
                    {totali && stagioneSelezionata && !isLoading && (
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Tasso conferma
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: totali.percentuale_confermato_totale > 50
                                            ? theme.palette.success.main
                                            : theme.palette.warning.main,
                                    }}
                                >
                                    {totali.percentuale_confermato_totale}%
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {/* Contenuto del grafico */}
                <Box sx={{
                    flexGrow: 1,
                    position: 'relative',
                    minHeight: { xs: 300, sm: 400 },
                    width: '100%',
                }}>
                    {isLoading && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 1,
                            borderRadius: 1,
                        }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!stagioneSelezionata && !isLoading && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                            <Typography variant="h6" color="text.secondary" align="center">
                                Seleziona una stagione
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center">
                                per visualizzare i budget per mandatario
                            </Typography>
                        </Box>
                    )}

                    {stagioneSelezionata && !isLoading && !hasData && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Nessun dato disponibile per la stagione selezionata
                            </Typography>
                        </Box>
                    )}

                    {stagioneSelezionata && !isLoading && hasData && (
                        <Box sx={{ width: '100%' }}>
                            <CustomLegend />

                            <Box sx={{ width: '100%', overflowY: 'auto', maxHeight: { xs: 500, lg: 800 } }}>
                                <ResponsiveContainer width="100%" height={chartHeight}>
                                    <BarChart
                                        data={chartData}
                                        layout="vertical"
                                        margin={{
                                            top: 10,
                                            right: 40,
                                            bottom: 10,
                                            left: 20,
                                        }}
                                        barCategoryGap="20%"
                                        barGap={2}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke={theme.palette.divider}
                                            horizontal={false}
                                        />
                                        <XAxis
                                            type="number"
                                            tickFormatter={(value) => formatCompact(value)}
                                            tick={{
                                                fontSize: 11,
                                                fill: theme.palette.text.secondary,
                                            }}
                                            label={{
                                                value: 'Valore (€)',
                                                position: 'insideBottom',
                                                offset: -5,
                                                style: {
                                                    textAnchor: 'middle',
                                                    fill: theme.palette.text.secondary,
                                                    fontSize: 12,
                                                },
                                            }}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="nome"
                                            width={140}
                                            tick={{
                                                fontSize: 11,
                                                fill: theme.palette.text.secondary,
                                            }}
                                        />
                                        <Tooltip content={<CustomTooltip />} />

                                        <Bar
                                            dataKey="valoreBudget"
                                            name="Budget"
                                            fill={theme.palette.warning.light}
                                            radius={[0, 4, 4, 0]}
                                        />
                                        <Bar
                                            dataKey="valoreConfermato"
                                            name="Confermato"
                                            fill={theme.palette.success.main}
                                            radius={[0, 4, 4, 0]}
                                        />
                                        <Bar
                                            dataKey="valoreProdotto"
                                            name="Prodotto"
                                            fill={theme.palette.primary.main}
                                            radius={[0, 4, 4, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default BudgetMandatarioBarChart;